import { useEffect, useRef } from 'react';
import styles from './Modal.module.css';

/**
 * Centred dialog over a blurred backdrop.
 *
 * Closes on Escape and on a click outside the card, locks background scrolling
 * while open, and moves focus into the dialog so keyboard users aren't left
 * behind on the page underneath.
 */
export function Modal({ open, onClose, title, children, className = '' }) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Return focus to whatever opened the dialog once it closes.
    const previouslyFocused = document.activeElement;
    cardRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        ref={cardRef}
        className={`${styles.card} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ✕
        </button>
        {title && <h3 className={styles.title}>{title}</h3>}
        {children}
      </div>
    </div>
  );
}

/** A label/value line inside a modal — the checkout order summary uses these. */
export function ModalRow({ label, value }) {
  return (
    <div className={styles.row}>
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}
